import React, { Suspense } from 'react';
import { func, object, oneOfType } from 'prop-types';

import GlobalProvider from '@context/GlobalProvider';
import useRefreshToken from '@helpers/fetcher/session/useTimeoutRefreshToken';
import { useNavigate } from 'react-router-dom';
import { useUserData } from '@services/users/me';
import useOnIdle from '@/hooks/useOnIdle';

import { authStorage, removeCookie } from '@ajaibid/utils';
import { COOKIE_KEY } from '@/config/constanta';
import TopNav from '../Layout/TopNav';
import stylesGlobal from '../screens/style';

import useRouteProp from './useRouteProp';
import useAuth from './useAuth';

function PrivateRoute({ component: Component, ...rest }) {
  useAuth();
  useRefreshToken();

  const { backgroundColor, scrollable, withHeaderAlt, className } = useRouteProp();
  const navigate = useNavigate();
  const { isLoading } = useUserData();

  const renderComponent = () => {
    let renderedComponent = <Component {...rest} />;

    if (withHeaderAlt) {
      const componentStyle = {
        overflowY: scrollable ? 'scroll' : 'initial',
        ...(backgroundColor ? { backgroundColor } : {}),
      };

      renderedComponent = (
        <div className={`maincontent-container-alt ${className}`} style={componentStyle}>
          {renderedComponent}
        </div>
      );
    }
    return renderedComponent;
  };

  useOnIdle({
    debounce: 500,
    idleTimeout: 300000, // idleTimeout in MiliSeconds (300000 = 5 minutes)
    onIdle: () => {
      removeCookie(COOKIE_KEY.access_token);
      removeCookie(COOKIE_KEY.refresh_token);
      authStorage.clear('xyz');
      navigate('/pin');
    },
    onAction: () => {},
    events: [
      'mousemove',
      'keydown',
      'wheel',
      'DOMMouseScroll',
      'mouseWheel',
      'mousedown',
      'visibilitychange',
    ],
  });

  return (
    <GlobalProvider>
      {!isLoading && (
        <div style={stylesGlobal.h100} data-testid="private-route-component">
          {withHeaderAlt && <TopNav style={{ boxShadow: '0 2px 4px 0 rgba(51, 51, 51, 0.16)' }} />}
          <Suspense fallback={null}>{renderComponent()}</Suspense>
        </div>
      )}
    </GlobalProvider>
  );
}

PrivateRoute.propTypes = {
  component: oneOfType([object, func]),
};

PrivateRoute.defaultProps = {
  component: null,
};

export default PrivateRoute;
